import React from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import styled from "styled-components/macro";

import UnverifiedEmailBanner from "../../../components/UnverifiedEmailBanner";
import { mediaQueries } from "../../../styles/media.js";
import { useRefreshDashboard, useSelectedCase } from "../utils/hooks";
import ButtonLink from "./ButtonLink";
import RequestGroupCollectionCell from "./RequestGroupCollectionCell";
import { collectionsOf, doableStatus, openCollections, openDoablesWithoutCollection } from "./requestGroupCollections";
import { canOpenDoable, doableHref } from "./doableLinks";
import ProbateLayout from "./ProbateLayout";
import ProbateMessages, { isCompletedTask, messageRows } from "./ProbateMessages";
import ProbateStatus from "./ProbateStatus";
import StatusMarker from "./StatusMarker";
import { Tray, TrayActions, TrayCell, TrayCells, TrayHeader } from "../../../probate/components/tray";
import { Badge } from "../../../probate/components/ui/badge";
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from "../../../probate/components/ui/empty";
import { Item, ItemContent, ItemGroup, ItemTitle } from "../../../probate/components/ui/item";

const Index = styled.nav`
  display: none;

  ${mediaQueries.xl} {
    display: block;
    position: sticky;
    top: var(--juno-space-6);
  }

  ul {
    list-style: none;
    margin: 0;
    padding: 0;
    border-bottom: 1px solid var(--border);
    font-size: var(--text-sm);
    line-height: var(--text-sm--line-height);
  }

  /* && outranks GlobalStyle's link colours and hover underline */
  && a:link,
  && a:visited,
  && a:hover,
  && a:active {
    display: flex;
    align-items: center;
    justify-content: space-between;
    /* The tray rows' spacing, so the index lines up with them */
    padding: var(--tray-cell-padding-block, var(--juno-space-4)) 0;
    border-bottom: 0;
    border-top: 1px solid var(--border);
    color: var(--foreground);
  }

  && li:first-child a {
    border-top: 0;
  }
`;

const ProbateCaseDetails = () => {
  const session = useSelector((state) => state.dashboardState.session);
  const selectedCase = useSelectedCase({ caseScreen: "case" });
  useRefreshDashboard({ staleTime: 0 });

  if (!selectedCase) {
    return null;
  }

  const collections = openCollections(collectionsOf(selectedCase), selectedCase.doables);
  const doablesWithoutCollection = openDoablesWithoutCollection(collectionsOf(selectedCase), selectedCase.doables);
  const thingsToDo = collections.length + doablesWithoutCollection.length;
  const hasCompletedTasks = selectedCase.doables.some(isCompletedTask);
  const canOpen = (doable) => canOpenDoable(session, doable);
  const otherClients = (selectedCase.other_clients ?? [])
    .map((client) => ({ ...client, doables: client.doables?.filter((d) => d.status === "available") ?? [] }))
    .filter((client) => client.doables.length > 0);
  const messages = messageRows(selectedCase);
  const unreadMessages = messages.filter((row) => row.unread).length;

  return (
    <>
      <ProbateLayout
        caseReference={selectedCase.case_reference}
        casePage
        index={
          <Index aria-label="On this page">
            <ul>
              <li>
                <a href="#things-to-do">
                  <span>Things to do</span>
                  {thingsToDo > 0 && <Badge>{thingsToDo}</Badge>}
                </a>
              </li>
              {messages.length > 0 && (
                <li>
                  <Link to={`/dashboard/${selectedCase.case_reference}/messages`}>
                    <span>Messages</span>
                    {unreadMessages > 0 && <Badge>{unreadMessages}</Badge>}
                  </Link>
                </li>
              )}
              {selectedCase.status_updates?.client_dashboard_overview && (
                <li>
                  <a href="#current-status">
                    <span>Your case</span>
                  </a>
                </li>
              )}
            </ul>
          </Index>
        }
      >
        {/* Only probate cases hold the ID check until the client verifies their email.
            Staff sessions get no email_verified, so an absent value hides the banner. */}
        {session.email_verified === false && <UnverifiedEmailBanner emailAddress={session.email_address} />}

        <Tray variant="card" headingId="things-to-do">
          <TrayHeader title="Things to do" count={thingsToDo || undefined} />
          {thingsToDo > 0 ? (
            <TrayCells>
              {collections.map((collection) => (
                <RequestGroupCollectionCell
                  key={collection.identifier}
                  caseRef={selectedCase.case_reference}
                  collection={collection}
                  doables={collection.doables}
                />
              ))}
              {doablesWithoutCollection.map((doable) =>
                canOpen(doable) ? (
                  // The /requests thanks pages read fromDashboard to offer a way back
                  <TrayCell
                    key={doable.id}
                    title={doable.title}
                    status={<StatusMarker status={doableStatus(doable)} />}
                    href={doableHref(doable)}
                    state={{ fromDashboard: true }}
                  />
                ) : (
                  <TrayCell
                    key={doable.id}
                    title={doable.title}
                    status={<StatusMarker status={doableStatus(doable)} />}
                    disabled
                  />
                ),
              )}
            </TrayCells>
          ) : (
            <Empty>
              <EmptyHeader>
                <EmptyTitle>You’re up to date</EmptyTitle>
                <EmptyDescription>We’ll let you know when something changes</EmptyDescription>
              </EmptyHeader>
            </Empty>
          )}
          {otherClients.map((client) => (
            <Tray key={client.email_address} variant="secondary">
              <TrayHeader title={`${client.first_name} has things to do`} count={client.doables.length} />
              {/* Another client's tasks can't be opened from this dashboard, so they're Items, not tray cells */}
              <ItemGroup>
                {client.doables.map((doable) => (
                  <Item key={doable.id} role="listitem">
                    <ItemContent>
                      <ItemTitle>{doable.title}</ItemTitle>
                    </ItemContent>
                  </Item>
                ))}
              </ItemGroup>
            </Tray>
          ))}
          {hasCompletedTasks && (
            <TrayActions>
              <ButtonLink to={`/dashboard/${selectedCase.case_reference}/things-youve-done`}>
                All completed tasks
              </ButtonLink>
            </TrayActions>
          )}
        </Tray>

        <ProbateMessages selectedCase={selectedCase} />

        <ProbateStatus selectedCase={selectedCase} />
      </ProbateLayout>
    </>
  );
};

export default ProbateCaseDetails;
